// ============================================================================
// src/pages/DashboardADM.tsx
// UPDATED: 2026-04-16 — fix revalidaRes destructuring + badge revalida
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Building2, UserCog, Users, BookOpen,
  Newspaper, CheckSquare, Bell, AlertTriangle,
  Settings, LogOut, Menu, X, ChevronRight, Shield,
  Clock, Loader2, GraduationCap, RefreshCw,
  Heart, MessageCircle,
  BarChart3,
} from 'lucide-react';

import apiClient, { TOKEN_KEY, USER_DATA_KEY } from '../services/api';

import DashboardResumoGeral           from '../components/Dashboard_ADM/DashboardResumoGeral';
import GerenciamentoProfissionais     from '../components/Dashboard_ADM/GerenciamentoProfissionais';
import GerenciamentoAlunos            from '../components/Dashboard_ADM/GerenciamentoAlunos';
import GerenciamentoCursos            from '../components/Dashboard_ADM/GerenciamentoCursosAdm';
import GerenciamentoNoticiasArtigos   from '../components/Dashboard_ADM/GerenciamentoNoticiasArtigos';
import GerenciamentoDiplomas          from '../components/Dashboard_ADM/GerenciamentoDiplomas';
import CentroNotificacoes             from '../components/Dashboard_ADM/CentroNotificacoes';
import GerenciamentoHomologacao       from '../components/Dashboard_ADM/GerenciamentoHomologacao';
import RelatorioEvasao                from '../components/Dashboard_ADM/RelatorioEvasao';
import ConfiguracoesPlataforma        from '../components/Dashboard_ADM/ConfiguracoesPlataforma';
import GerenciamentoNovidades         from '../components/Dashboard_ADM/GerenciamentoNovidades';
import GerenciamentoRevalida          from '../components/Dashboard_ADM/GerenciamentoRevalida';
import GerenciamentoProfissionaisADM  from '../components/Dashboard_ADM/GerenciamentoProfissionaisADM';
import GerenciamentoAcoesSociais from '../components/Dashboard_ADM/GerenciamentoAcoesSociais';
import Atendimento from '../components/Dashboard_ADM/Atendimento';
import FiliationAnalytics from '../components/Dashboard_ADM/FiliationAnalytics';
// ─── Types ────────────────────────────────────────────────────────────────────
interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  component: React.FC;
  badge?: number;
  group?: string;
}

interface AdminUser {
  id: string; fullName: string; email: string; userType: string;
}

interface BadgeCounts {
  revalidaPendentes:  number;
  affiliatesPending:  number;
  coursesPending:     number;
  notificationsUnread: number;
  evasaoAtivos:       number;
  diplomasPendentes:  number;
}

interface ProfileResponse {
  id: string; fullName?: string; full_name?: string; email: string; userType?: string; user_type?: string;
}
interface AffiliateStatsResponse { pending?: number }
interface NotifUnreadResponse    { count?: number; notifications?: unknown[] }
interface CourseStatsResponse    { pending?: number; pendingCount?: number }
interface EvasionStatsResponse   { unresolved?: number }
interface RevalidaStatsResponse  { pending?: number }

// ─── Hook ─────────────────────────────────────────────────────────────────────
function useAdminData() {
  const [adminUser, setAdminUser] = useState<AdminUser>({
    id: '', fullName: 'Administrador', email: 'admin@federacao.com', userType: 'admin',
  });
  const [badges, setBadges] = useState<BadgeCounts>({
    revalidaPendentes:   0,
    affiliatesPending:   0,
    coursesPending:      0,
    notificationsUnread: 0,
    evasaoAtivos:        0,
    diplomasPendentes:   0,
  });
  const [loadingBadges, setLoadingBadges] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const data = await apiClient.post<ProfileResponse>('/auth/me');
      setAdminUser({
        id:       data.id,
        fullName: data.fullName ?? data.full_name ?? 'Administrador',
        email:    data.email,
        userType: data.userType ?? data.user_type ?? 'admin',
      });
    } catch { /* mantém fallback */ }
  }, []);

  const fetchBadges = useCallback(async () => {
    setLoadingBadges(true);
    try {
      // ✅ 6 variáveis para 6 promises — corrigido
      const [affiliatesRes, notifRes, coursesRes, evasionRes, diplomasRes, revalidaRes] =
        await Promise.allSettled([
          apiClient.get<AffiliateStatsResponse>('/affiliates/stats/summary'),
          apiClient.get<NotifUnreadResponse>('/notifications/unread'),
          apiClient.get<CourseStatsResponse>('/admin/stats/courses'),
          apiClient.get<EvasionStatsResponse>('/evasion/stats'),
          apiClient.get<any>('/students/diploma-requests/all'),
          apiClient.get<RevalidaStatsResponse>('/revalida/stats/summary'),
        ]);

      if (affiliatesRes.status === 'fulfilled')
        setBadges(p => ({ ...p, affiliatesPending: affiliatesRes.value.pending ?? 0 }));

      if (notifRes.status === 'fulfilled')
        setBadges(p => ({ ...p, notificationsUnread: notifRes.value.count ?? 0 }));

      if (coursesRes.status === 'fulfilled')
        setBadges(p => ({ ...p, coursesPending: coursesRes.value.pending ?? coursesRes.value.pendingCount ?? 0 }));

      if (evasionRes.status === 'fulfilled')
        setBadges(p => ({ ...p, evasaoAtivos: evasionRes.value.unresolved ?? 0 }));

      // ✅ revalidaRes agora existe
      if (revalidaRes.status === 'fulfilled')
        setBadges(p => ({ ...p, revalidaPendentes: revalidaRes.value.pending ?? 0 }));

      if (diplomasRes.status === 'fulfilled') {
        const items: any[] = diplomasRes.value.data ?? [];
        let pendentes = 0;
        for (const s of items) {
          if (s.diplomaRequests?.some((r: any) => r.status === 'pending')) pendentes++;
        }
        setBadges(p => ({ ...p, diplomasPendentes: pendentes }));
      }
    } catch { /* mantém zeros */ }
    finally { setLoadingBadges(false); }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchBadges();
    const interval = setInterval(fetchBadges, 60_000);
    return () => clearInterval(interval);
  }, [fetchProfile, fetchBadges]);

  return { adminUser, badges, loadingBadges, refetchBadges: fetchBadges };
}

// ─── Component ────────────────────────────────────────────────────────────────
const DashboardADM: React.FC = () => {
  const navigate = useNavigate();
  const [menuAberto, setMenuAberto] = useState(false);
  const [secaoAtual, setSecaoAtual] = useState('resumo');

  const { adminUser, badges, loadingBadges, refetchBadges } = useAdminData();

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const raw   = localStorage.getItem(USER_DATA_KEY);
    const user  = raw ? JSON.parse(raw) : null;
    if (!token || user?.role !== 'admin') navigate('/login-adm');
  }, [navigate]);

  const menuItems: MenuItem[] = [
    { id: 'resumo',        label: 'Resumo Geral',       icon: LayoutDashboard, component: DashboardResumoGeral,          group: 'Principal'   },
    { id: 'atendimento',   label: 'Atendimento',        icon: MessageCircle,   component: Atendimento,                   group: 'Principal'   },

    { id: 'filiados',      label: 'Filiados/Escolas',   icon: UserCog,         component: GerenciamentoProfissionais,    group: 'Cadastros'   },
    { id: 'profissionais', label: 'Profissionais',      icon: Building2,       component: GerenciamentoProfissionaisADM, group: 'Cadastros'   },
    { id: 'alunos',        label: 'Alunos',             icon: Users,           component: GerenciamentoAlunos,           group: 'Cadastros'   },

    { id: 'diplomas',      label: 'Diplomas',           icon: GraduationCap,   component: GerenciamentoDiplomas,         group: 'Documentos', badge: badges.diplomasPendentes  || undefined },
    { id: 'revalida',      label: 'Revalida',           icon: RefreshCw,       component: GerenciamentoRevalida,         group: 'Documentos', badge: badges.revalidaPendentes  || undefined },
    { id: 'homologacao',   label: 'Homologação',        icon: CheckSquare,     component: GerenciamentoHomologacao,      group: 'Documentos'  },

    { id: 'cursos',        label: 'Cursos',             icon: BookOpen,        component: GerenciamentoCursos,           group: 'Conteúdo',   badge: badges.coursesPending || undefined },
    { id: 'noticias',      label: 'Notícias/Novidades', icon: Newspaper,       component: GerenciamentoNoticiasArtigos,  group: 'Conteúdo'    },
    { id: 'acoes-sociais', label: 'Ações Sociais',      icon: Heart,           component: GerenciamentoAcoesSociais,     group: 'Conteúdo'    },
    { id: 'novidades',     label: 'Parceiros',          icon: Newspaper,       component: GerenciamentoNovidades,        group: 'Conteúdo'    },

    { id: 'notificacoes',  label: 'Notificações',       icon: Bell,            component: CentroNotificacoes,            group: 'Operacional', badge: badges.notificationsUnread || undefined },
    { id: 'funil-filiacao', label: 'Funil de Filiação', icon: BarChart3,        component: FiliationAnalytics,             group: 'Operacional' },
    { id: 'evasao',        label: 'Relatório Evasão',   icon: AlertTriangle,   component: RelatorioEvasao,               group: 'Operacional', badge: badges.evasaoAtivos       || undefined },

    { id: 'configuracoes', label: 'Configurações',      icon: Settings,        component: ConfiguracoesPlataforma,       group: 'Sistema'     },
  ];

  const groups = ['Principal', 'Cadastros', 'Documentos', 'Conteúdo', 'Operacional', 'Sistema'];
  const SecaoAtualComponent = menuItems.find(i => i.id === secaoAtual)?.component ?? DashboardResumoGeral;
  const tituloAtual         = menuItems.find(i => i.id === secaoAtual)?.label     ?? 'Dashboard';
  const inicialNome         = adminUser.fullName.charAt(0).toUpperCase();

  const handleLogout = () => {
    ['auth_token', 'refresh_token', 'user_data'].forEach(k => localStorage.removeItem(k));
    navigate('/login-adm');
  };

  const handleChangeSection = (id: string) => {
    setSecaoAtual(id);
    setMenuAberto(false);
    refetchBadges();
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-white/[0.06]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gold-500/15 border border-gold-400/20 flex items-center justify-center">
            <Shield className="w-5 h-5 text-gold-400" strokeWidth={1.75}/>
          </div>
          <div>
            <p className="text-white font-semibold text-base leading-none tracking-tight">Federação</p>
            <p className="text-white/40 text-xs mt-1">Hub Admin</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-5 px-3 space-y-5">
        {groups.map(group => {
          const items = menuItems.filter(m => m.group === group);
          return (
            <div key={group}>
              <p className="text-white/45 text-[10px] font-semibold uppercase tracking-widest px-3 mb-2">{group}</p>
              <div className="space-y-0.5">
                {items.map(item => {
                  const Icon  = item.icon;
                  const ativo = secaoAtual === item.id;
                  return (
                    <button key={item.id} onClick={() => handleChangeSection(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 relative ${
                        ativo
                          ? 'bg-white/[0.07] text-white border border-gold-400/25'
                          : 'text-white/80 hover:bg-white/[0.04] hover:text-white border border-transparent'
                      }`}>
                      <Icon className={`w-4 h-4 flex-shrink-0 ${ativo ? 'text-gold-400' : ''}`} strokeWidth={1.75}/>
                      <span className="text-sm font-medium flex-1 text-left">{item.label}</span>
                      {!!item.badge && item.badge > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-rose-500/90 text-white min-w-[18px] text-center">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="p-4 border-t border-white/[0.06]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-gradient-gold flex items-center justify-center flex-shrink-0">
            <span className="text-cosmos-900 font-semibold text-sm">{inicialNome}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium text-sm truncate">{adminUser.fullName}</p>
            <p className="text-white/40 text-xs truncate">{adminUser.email}</p>
          </div>
        </div>
        <button onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-white/50 hover:bg-rose-500/10 hover:text-rose-300 transition-all text-sm font-medium">
          <LogOut className="w-4 h-4"/> Sair
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      <aside className="hidden lg:flex w-64 flex-shrink-0 bg-gradient-cosmos flex-col">
        <SidebarContent/>
      </aside>

      {menuAberto && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setMenuAberto(false)}/>
          <aside className="fixed left-0 top-0 h-full w-72 bg-gradient-cosmos z-50 flex flex-col lg:hidden shadow-2xl">
            <div className="absolute top-4 right-4">
              <button onClick={() => setMenuAberto(false)} className="text-white/60 hover:text-white p-1">
                <X className="w-6 h-6"/>
              </button>
            </div>
            <SidebarContent/>
          </aside>
        </>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white/70 backdrop-blur-xl border-b border-cosmos-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <button onClick={() => setMenuAberto(true)} className="lg:hidden p-2 rounded-xl text-cosmos-600 hover:bg-cosmos-50">
              <Menu className="w-5 h-5"/>
            </button>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-cosmos-400 font-medium">Admin</span>
              <ChevronRight className="w-3.5 h-3.5 text-cosmos-300"/>
              <span className="text-cosmos-900 font-semibold">{tituloAtual}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {loadingBadges && <Loader2 className="w-4 h-4 text-cosmos-300 animate-spin"/>}
            <div className="hidden md:flex items-center gap-2 text-sm text-cosmos-400">
              <Clock className="w-4 h-4"/>
              <span>{new Date().toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' })}</span>
            </div>
            <button onClick={() => handleChangeSection('notificacoes')} className="relative p-2 rounded-xl text-cosmos-500 hover:bg-cosmos-50">
              <Bell className="w-5 h-5"/>
              {badges.notificationsUnread > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 rounded-full text-white text-[10px] font-semibold flex items-center justify-center">
                  {badges.notificationsUnread > 9 ? '9+' : badges.notificationsUnread}
                </span>
              )}
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-gold flex items-center justify-center">
                <span className="text-cosmos-900 font-semibold text-xs">{inicialNome}</span>
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-semibold text-cosmos-900 leading-none">{adminUser.fullName}</p>
                <p className="text-xs text-cosmos-400 leading-none mt-0.5">{adminUser.userType}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <SecaoAtualComponent/>
        </main>
      </div>
    </div>
  );
};

export default DashboardADM;
